import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { mapAuthError } from '@mybusinessmate/api-client';
import { FieldGrid, SettingCard } from '../../../../../components/settings/setting-card';
import { UnsavedBar } from '../../../../../components/settings/unsaved-bar';
import { Avatar } from '../../../../../components/ui/avatar';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { Select } from '../../../../../components/ui/select';
import { Skeleton } from '../../../../../components/ui/skeleton';
import { useToast } from '../../../../../components/ui/toast';
import { authClient } from '../../../../../lib/api';
import { initialsOf } from '../../../../../lib/initials';
import { sessionQueryOptions, useSession } from '../../../../../lib/session';
import { isStaffRoleName, ROLE_LABEL } from '../../../../../lib/staff-nav';
import {
  DATE_FORMATS,
  formatDatePattern,
  LANGUAGES,
  staffProfileFormSchema,
  TIMEZONES,
  WEEK_STARTS,
  type StaffProfileForm,
} from '../../../../../lib/staff-profile-form';
import { staffMeQuery, useStaffPreferences } from '../../../../../lib/workspace-api';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/profile')({
  component: ProfilePage,
});

function ProfilePage() {
  const { data: session } = useSession();
  const user = session?.user;
  const prefs = useStaffPreferences('profile');
  const me = useQuery(staffMeQuery);
  const role = isStaffRoleName(user?.role) ? user.role : undefined;

  if (!user || !prefs.data) {
    return <Skeleton className="h-(--mbm-layout-dialog-wide-width) rounded-(--mbm-radius-lg)" />;
  }
  // Remount the form when the signed-in account (and so its saved values) changes.
  return (
    <ProfileForm
      key={user.id}
      name={user.name}
      email={user.email}
      roleLabel={role ? ROLE_LABEL[role] : ''}
      team={[me.data?.department, me.data?.title].filter(Boolean).join(' · ') || '—'}
      saved={prefs.data}
      onSavePrefs={(value) => prefs.save.mutateAsync(value)}
    />
  );
}

function ProfileForm({
  name,
  email,
  roleLabel,
  team,
  saved,
  onSavePrefs,
}: {
  name: string;
  email: string;
  roleLabel: string;
  team: string;
  saved: Omit<StaffProfileForm, 'fullName'>;
  onSavePrefs: (value: Omit<StaffProfileForm, 'fullName'>) => Promise<unknown>;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const values: StaffProfileForm = {
    fullName: name,
    displayName: saved.displayName || name.split(/\s+/)[0] || '',
    phone: saved.phone,
    timezone: saved.timezone,
    language: saved.language,
    dateFormat: saved.dateFormat,
    weekStartsOn: saved.weekStartsOn,
  };

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<StaffProfileForm>({
    resolver: zodResolver(staffProfileFormSchema),
    mode: 'onChange',
    values,
    resetOptions: { keepDirtyValues: false },
  });

  const onSave = handleSubmit(async (form) => {
    if (form.fullName !== name) {
      const { error } = await authClient.updateUser({ name: form.fullName });
      const mapped = mapAuthError(error);
      if (mapped) {
        setError('fullName', { message: mapped.message });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: sessionQueryOptions.queryKey });
    }
    try {
      await onSavePrefs({
        displayName: form.displayName,
        phone: form.phone,
        timezone: form.timezone,
        language: form.language,
        dateFormat: form.dateFormat,
        weekStartsOn: form.weekStartsOn,
      });
    } catch {
      return; // the data layer already told the person why
    }
    reset(form);
    toast({ title: 'Profile saved' });
  });

  const invalid = Object.keys(errors).length;
  const fullName = watch('fullName');

  return (
    <>
      <SettingCard
        title="Your profile"
        subtitle="This is how clients and teammates see you in chats, meetings and reviews."
      >
        <div className="flex flex-wrap items-center gap-(--mbm-space-4) pb-(--mbm-space-4)">
          <Avatar initials={initialsOf(fullName, email)} size="xl" className="bg-brand-primary" label={fullName} />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-heading-h3 text-text-strong">{fullName}</span>
            <span className="truncate text-body-small text-text-secondary">
              {[roleLabel, email].filter(Boolean).join(' · ')}
            </span>
          </div>
          {/* TODO(api): staff profile photos need an upload category and a profile endpoint. */}
          <Button
            variant="secondary"
            size="row"
            onClick={() => toast({ title: 'Profile photos are coming soon', tone: 'info' })}
          >
            Change photo
          </Button>
          <Button variant="ghost" size="row" onClick={() => toast({ title: 'No photo to remove', tone: 'info' })}>
            Remove
          </Button>
        </div>
        <FieldGrid>
          <Input
            variant="filled"
            label="Full name"
            autoComplete="name"
            error={errors.fullName?.message}
            {...register('fullName')}
          />
          <Input
            variant="filled"
            label="Display name in chat"
            error={errors.displayName?.message}
            {...register('displayName')}
          />
          <Input variant="filled" label="Work email" value={email} readOnly />
          <Input
            variant="filled"
            label="Phone"
            type="tel"
            autoComplete="tel"
            error={errors.phone?.message}
            {...register('phone')}
          />
        </FieldGrid>
        <p className="pt-(--mbm-space-4) text-body-small text-text-tertiary">
          Work email is set by your admin and can’t be changed here.
        </p>
      </SettingCard>

      <SettingCard title="Role & team" subtitle="Set by an admin in Staff. Ask them if something’s wrong.">
        <FieldGrid>
          <Input variant="filled" label="Role" value={roleLabel} readOnly />
          <Input variant="filled" label="Team" value={team} readOnly />
        </FieldGrid>
      </SettingCard>

      <SettingCard title="Region & language">
        <FieldGrid>
          <Select variant="filled" label="Timezone" {...register('timezone')}>
            {TIMEZONES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
          <Select variant="filled" label="Language" {...register('language')}>
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </Select>
          <Select variant="filled" label="Date format" {...register('dateFormat')}>
            {DATE_FORMATS.map((f) => (
              <option key={f} value={f}>
                {formatDatePattern(new Date(), f)}
              </option>
            ))}
          </Select>
          <Select variant="filled" label="Week starts on" {...register('weekStartsOn')}>
            {WEEK_STARTS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </Select>
        </FieldGrid>
      </SettingCard>

      <UnsavedBar
        visible={isDirty}
        message={
          invalid > 0
            ? `${invalid} ${invalid === 1 ? 'field needs' : 'fields need'} fixing before you can save`
            : undefined
        }
        saveDisabled={invalid > 0}
        saving={isSubmitting}
        onDiscard={() => reset(values)}
        onSave={() => void onSave()}
      />
    </>
  );
}
